import { Component, OnInit, OnChanges, OnDestroy, Optional, forwardRef, ViewChild } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Subscription } from 'rxjs';

import { languageSupport } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';

type I18nMap = Partial<Record<string, string>>;

interface ErrorInfo {
    [key: string]: string;
}

/******************************************/
/* UPP-KB-LANGUAGE-TEXTAREA COMPONENT     */
/******************************************/

/**
 * Raw multi-line i18n text field.
 *
 * Carries the multilingual behaviour: its control value is a map (`{ [languageCode]: string }`)
 * and it edits only the entry for the active `lang` through {@link InputForm} (one
 * {@link FormControl} per supported language). It renders a bare {@link UppKbTextAreaComponent}
 * (no title, no error chrome), so it can be embedded inline, while
 * {@link UppLanguageTextAreaComponent} wraps it to add title and validation messages.
 *
 * @selector `upp-kb-language-textarea`
 */
@Component({
    selector: 'upp-kb-language-textarea',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './kb/upp-textarea.html',
    styleUrls: [ './kb/upp-textarea.scss' ],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => UppKbLanguageTextAreaComponent),
        multi: true,
    }],
})
export class UppKbLanguageTextAreaComponent extends languageSupport implements ControlValueAccessor, OnChanges, OnDestroy {

    /************************************/
    /* INPUTS AND OUTPUTS               */
    /************************************/

    @Input() placeholder = '';
    @Input() readonly = false;
    @Input() disabled = false;
    @Input() kiosk = true;
    @Input() lang = 'ES';
    @Input() name = 'none';
    @Input() rows = 1;

    @Output() Changed = new EventEmitter<I18nMap | null>();

    /************************************/
    /* SUBSCRIPTIONS                    */
    /************************************/

    private _inputForm_subscription: Subscription | null = null;

    /************************************/
    /* CLASS VARIABLES                  */
    /************************************/

    private _value: I18nMap | null = null;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback: (v: I18nMap | null) => void = (_v) => {
        // required by angular
    };
    private _onTouchCallback: () => void = () => {
        // required by angular
    };
    /* eslint-enable @typescript-eslint/no-unused-vars */

    /************************************/
    /* UI GETTERS                       */
    /************************************/

    get Languages(): string[] {
        return this._lang.GetLanguages();
    }

    get Control(): FormControl<string | null> {
        return this.InputForm.get(this.lang) as FormControl<string | null>;
    }

    /** Placeholder for the active column: default translation when empty, otherwise the caller hint. */
    get content(): string {
        const _active = (this._value?.[this.lang] ?? '').toString().trim();
        if (_active !== ''){
            return this.placeholder;
        }

        if (this._value){
            for (const _lang of this._lang.GetLanguages()){
                const _text = this._value[_lang];
                if (_text !== null && _text !== undefined && String(_text).trim() !== ''){
                    return String(_text);
                }
            }
        }
        
        return this.placeholder;
    }

    /********************************************/
    /* disable control                          */
    /********************************************/

    get IsDisabled(): boolean {
        return this.disabled;
    }

    set IsDisabled(disabled: boolean) {
        if (this._inputForm == null){
            return;
        }

        const _control = this._inputForm.get(this.lang);
        if (!_control){
            return;
        }

        if (disabled){
            _control.disable({ emitEvent: false });
        }
        else {
            _control.enable({ emitEvent: false });
        }
    }

    /************************************/
    /* INPUT FORM                       */
    /************************************/

    private _inputForm: FormGroup | null = null;
    get InputForm(): FormGroup {
        if (this._inputForm == null){
            this._inputForm = this.LoadInputForm();
        }
        return this._inputForm;
    }

    private LoadInputForm(): FormGroup {
        const _patch: Record<string, string | null> = {};

        for (const _code of this.Languages){
            _patch[_code] = this._value?.[_code] ?? null;
        }

        let _form: FormGroup | null = this._inputForm;
        if (_form == null){
            const _controls: Record<string, FormControl<string | null>> = {};
            for (const _code of this.Languages){
                _controls[_code] = new FormControl<string | null>(_patch[_code] ?? null);
            }
            _form = new FormGroup(_controls);

            this._inputForm_subscription = _form.valueChanges.subscribe(() => {
                this.OnChanged();
            });
        }
        else {
            _form.patchValue(_patch, { emitEvent: false });
        }

        this._inputForm = _form;
        this.IsDisabled = this.disabled;
        this.change.markForCheck();

        return _form;
    }

    /************************************/
    /* CONSTRUCTOR AND LIFECYCLE        */
    /************************************/

    constructor(
        private change: ChangeDetectorRef,
        lang: languageService,
    ) {
        super(lang, change);
    }

    ngOnChanges(): void {
        this.IsDisabled = this.disabled;
        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        this._inputForm_subscription?.unsubscribe();
        this._inputForm_subscription = null;

        super.OnDestroy();
    }

    /************************************/
    /* PUBLIC HANDLERS                  */
    /************************************/

    OnChanged(): void {
        const _form = this._inputForm;
        if (!_form){
            return;
        }

        const _map: I18nMap = {};
        let _hasText = false;
        for (const _code of this.Languages){
            const _text = (_form.get(_code)?.value ?? '').toString();
            if (_text.trim() !== ''){
                _map[_code] = _text;
                _hasText = true;
            }
        }
        const _next = _hasText ? _map : null;

        if (JSON.stringify(this._value) === JSON.stringify(_next)){
            return;
        }

        this._value = _next;
        this._onChangeCallback(this._value);
        this._onTouchCallback();
        this.Changed.emit(this._value);
        this.change.markForCheck();
    }

    /************************************/
    /* GET/SET ACCESSORS                */
    /************************************/

    @Input()
    get value(): I18nMap | null {
        return this._value;
    }

    set value(v: I18nMap | null) {
        if (JSON.stringify(v) === JSON.stringify(this._value)){
            return;
        }

        this._value = v;
        this._inputForm = this.LoadInputForm();
        this.change.markForCheck();
    }

    /************************************/
    /* CONTROL VALUE ACCESSOR           */
    /************************************/

    writeValue(value: I18nMap | null): void {
        this.value = value;
    }

    registerOnChange(fn: (v: I18nMap | null) => void): void {
        this._onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this._onTouchCallback = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.IsDisabled = isDisabled;
    }
}

/**********************************/
/* UPP-LANGUAGE-TEXTAREA COMPONENT */
/**********************************/

/**
 * Multi-line i18n text field.
 *
 * Looks and behaves like {@link UppTextAreaComponent}, but its control value is a multilingual
 * map (`{ [languageCode]: string }`). It is a thin wrapper that delegates the multilingual
 * editing to {@link UppKbLanguageTextAreaComponent} (mirroring how `upp-textarea` wraps
 * `upp-kb-textarea`) and adds the title label plus the validation messages, so the caller binds
 * the map through `formControlName` exactly as a plain text control.
 *
 * @selector `upp-language-textarea`
 */
@Component({
    selector: 'upp-language-textarea',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-textarea.html',
    styleUrls: [ './upp-textarea.scss' ],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => UppLanguageTextAreaComponent),
        multi: true,
    }],
})
export class UppLanguageTextAreaComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

    /************************************/
    /* INPUTS AND OUTPUTS               */
    /************************************/

    @Input() formControlName = '';
    @Input() placeholder = '';
    @Input() title = '';
    @Input() readonly = false;
    @Input() disabled = false;
    @Input() kiosk = true;
    @Input() errornfo: ErrorInfo = {};
    @Input() lang = 'ES';
    @Input() rows = 1;

    @Output() Changed = new EventEmitter<I18nMap | null>();

    /************************************/
    /* SUBSCRIPTIONS                    */
    /************************************/

    private _form_subscription: Subscription | null = null;

    /************************************/
    /* CLASS VARIABLES                  */
    /************************************/

    @ViewChild(UppKbLanguageTextAreaComponent) private _raw: UppKbLanguageTextAreaComponent | null = null;

    formGroup: FormGroup | null = null;
    formControl: AbstractControl | null = null;

    private _value: I18nMap | null = null;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback: (v: I18nMap | null) => void = (_v) => {
        // required by angular
    };
    private _onTouchCallback: () => void = () => {
        // required by angular
    };
    /* eslint-enable @typescript-eslint/no-unused-vars */

    /************************************/
    /* UI GETTERS                       */
    /************************************/

    get showTitle(): boolean {
        return Object.values(this._value ?? {}).some((text) => (text ?? '').toString().trim().length > 0);
    }

    get viewTitle(): string {
        return this.title || this.placeholder;
    }

    /** Active-language text, used to force the error component to re-evaluate on edits. */
    get activeText(): string | null {
        return this._value?.[this.lang] ?? null;
    }

    /********************************************/
    /* disable control                          */
    /********************************************/

    get _disabled(): boolean {
        if (this.disabled){
            return true;
        }

        const _formcontrol = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (_formcontrol){
            return _formcontrol.disabled;
        }

        return false;
    }

    /************************************/
    /* CONSTRUCTOR AND LIFECYCLE        */
    /************************************/

    constructor(
        @Optional() private controlContainer: ControlContainer,
        private change: ChangeDetectorRef,
    ) {
        // nothing to do
    }

    ngOnInit(): void {
        if (this.controlContainer instanceof FormGroupDirective){
            this.formGroup = this.controlContainer.form;
        }

        this.formControl = (this.formGroup && this.formControlName) ? this.formGroup.get(this.formControlName) : null;
        if (this.formControl){
            this._value = this.formControl.value as I18nMap | null;

            this._form_subscription = this.formControl.valueChanges.subscribe(() => {
                const _incoming = this.formControl?.value as I18nMap | null;
                if (JSON.stringify(this._value) === JSON.stringify(_incoming)){
                    return;
                }
                this._value = _incoming;
                this._raw?.writeValue(_incoming);
                this.change.markForCheck();
            });
        }
    }

    ngOnChanges(): void {
        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        this._form_subscription?.unsubscribe();
        this._form_subscription = null;
    }

    /************************************/
    /* GET/SET ACCESSORS                */
    /************************************/

    get value(): I18nMap | null {
        return this._value;
    }

    set value(v: I18nMap | null) {
        if (JSON.stringify(v) === JSON.stringify(this._value)){
            return;
        }

        this._value = v;
        this._onChangeCallback(v);
        this._onTouchCallback();
        this.Changed.emit(v);
        this.change.markForCheck();
    }

    /************************************/
    /* CONTROL VALUE ACCESSOR           */
    /************************************/

    writeValue(value: I18nMap | null): void {
        if (JSON.stringify(value) === JSON.stringify(this._value)){
            return;
        }

        this._value = value;
        this._raw?.writeValue(value);
        this.change.markForCheck();
    }

    registerOnChange(fn: (v: I18nMap | null) => void): void {
        this._onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this._onTouchCallback = fn;
    }
}
